import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Clock, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { EbookInline } from "../components/EbookPromo";

const SITE = "https://infantry-financial.com";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    window.scrollTo(0, 0);
    api.get(`/blog/posts/${slug}`)
      .then(({ data }) => { setPost(data.post); setRelated(data.related || []); })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <section className="section bg-[var(--if-bg)]">
        <div className="container-if text-center py-20">
          <h1 className="font-display text-3xl mb-4">Article not found.</h1>
          <Link to="/blog" className="btn-gold" data-testid="blog-back">Back to all articles</Link>
        </div>
      </section>
    );
  }
  if (!post) {
    return (
      <section className="section bg-[var(--if-bg)]">
        <div className="container-if text-center py-20 text-white/50 text-sm" style={{ fontFamily: "Inter, sans-serif" }}>Loading…</div>
      </section>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.meta_description || post.excerpt,
    image: post.cover_image || undefined,
    datePublished: post.published_at,
    dateModified: post.updated_at,
    author: { "@type": "Organization", name: "Infantry Financial" },
    publisher: { "@type": "Organization", name: "Infantry Financial" },
    mainEntityOfPage: `${SITE}/blog/${post.slug}`,
  };

  return (
    <>
      <Helmet>
        <title>{`${post.meta_title || post.title} | Infantry Financial`}</title>
        <meta name="description" content={post.meta_description || post.excerpt} />
        <link rel="canonical" href={`${SITE}/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.meta_title || post.title} />
        <meta property="og:description" content={post.meta_description || post.excerpt} />
        {post.cover_image && <meta property="og:image" content={post.cover_image} />}
        <meta property="og:url" content={`${SITE}/blog/${post.slug}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <section className="inverse bg-[#23261C] relative pt-16 pb-10">
        <div className="absolute inset-0">
          {post.cover_image && (
            <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover object-top" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#1B211A] via-[#1B211A]/85 to-[#1B211A]/60"></div>
        </div>
        <div className="relative z-10 container-if">
          <Link to="/blog" className="inline-flex items-center gap-2 text-white/60 hover:text-[var(--if-gold)] text-sm mb-8" data-testid="blog-back">
            <ArrowLeft className="w-4 h-4" /> All Articles
          </Link>
          <span className="chip mb-6">{post.category}</span>
          <h1 className="font-display text-4xl md:text-6xl text-white max-w-4xl leading-tight mb-5">{post.title}</h1>
          <div className="flex items-center gap-5 text-white/60 text-sm" style={{ fontFamily: "Inter, sans-serif" }}>
            <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-[var(--if-gold)]" /> {post.read_time}</span>
            <span>·</span>
            <span>Infantry Financial Team</span>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[var(--if-bg)]">
        <div className="container-if max-w-3xl">
          <p className="text-[#1B211A]/80 text-xl leading-relaxed mb-10 font-display italic">{post.excerpt}</p>
          <div className="blog-content" data-testid="blog-post-content">
            <ReactMarkdown
              components={{
                h2: ({ children }) => <h2 className="font-display text-2xl md:text-3xl text-[#1B211A] mt-10 mb-3">{children}</h2>,
                h3: ({ children }) => <h3 className="font-display text-xl text-[#1B211A] mt-8 mb-2">{children}</h3>,
                p: ({ children }) => <p className="text-[#1B211A]/75 text-base leading-relaxed mb-5" style={{ fontFamily: "Inter, sans-serif" }}>{children}</p>,
                ul: ({ children }) => <ul className="list-disc pl-6 mb-5 text-[#1B211A]/75 text-base leading-relaxed space-y-2" style={{ fontFamily: "Inter, sans-serif" }}>{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal pl-6 mb-5 text-[#1B211A]/75 text-base leading-relaxed space-y-2" style={{ fontFamily: "Inter, sans-serif" }}>{children}</ol>,
                strong: ({ children }) => <strong className="text-[#1B211A] font-semibold">{children}</strong>,
                a: ({ href, children }) => <a href={href} className="text-[#4F5D1E] underline">{children}</a>,
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>

          <EbookInline />

          <div className="card-cream p-8 mt-12 text-[var(--if-ink)]">
            <div className="font-mono-label mb-2" style={{ color: "#4F5D1E" }}>Ready to move?</div>
            <h3 className="font-display text-2xl mb-3">Get a free review of your actual credit file.</h3>
            <p className="text-sm mb-5 text-[rgba(27,29,25,0.7)]" style={{ fontFamily: "Inter, sans-serif" }}>
              An article only goes so far. Let's look at your exact report together and build a real plan.
            </p>
            <Link to="/booking" className="btn-gold" data-testid="blog-cta-book">
              Book Free Consultation <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container-if">
            <h3 className="font-display text-2xl md:text-3xl text-white mb-8">More Intel</h3>
            <div className="grid sm:grid-cols-3 gap-5">
              {related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="card-dark p-5 block" data-testid={`related-${r.slug}`}>
                  <span className="text-[10px] font-semibold tracking-widest uppercase text-[var(--if-gold)]" style={{ fontFamily: "Inter, sans-serif" }}>{r.category}</span>
                  <h4 className="font-display text-white text-lg mt-2 mb-2">{r.title}</h4>
                  <p className="text-white/50 text-sm" style={{ fontFamily: "Inter, sans-serif" }}>{(r.excerpt || "").slice(0, 90)}…</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
